import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Sparkles, Activity, TrendingUp, Music as MusicIcon, Mic, ShoppingCart, Megaphone,
  Users, FileText, Server, AlertCircle, ArrowUpRight, Hourglass, RefreshCcw,
  CircleDollarSign, BadgeCheck, Database, Clock, BookOpen, Headphones, Star,
} from 'lucide-react';
import { api } from '@/lib/api';

const ACCENTS = {
  emerald: 'var(--pg-emerald)',
  gold: 'var(--pg-gold-2)',
  pink: 'var(--pg-neon-pink)',
};

function formatUsd(v) {
  if (v === null || v === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));
  } catch { return `$${v}`; }
}

function formatValue(metric) {
  if (!metric) return '—';
  if (metric.value === null || metric.value === undefined) return '—';
  if (metric.unit === 'USD') return formatUsd(metric.value);
  if (metric.unit === '%') return `${metric.value}%`;
  if (typeof metric.value === 'object') return null;
  return String(metric.value);
}

/* =====================================================
   Small primitive cards (reused throughout the dashboard)
   ===================================================== */
function SectionShell({ icon: Icon, title, subtitle, accent = 'emerald', children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <div className="pg-admin-bar flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${ACCENTS[accent]} 0%, rgba(7,11,20,0.6) 130%)`,
              boxShadow: `0 6px 20px -6px ${ACCENTS[accent]}`,
            }}
          >
            <Icon size={15} color="#0a0712" />
          </span>
          <div className="min-w-0">
            <p className="pg-label text-[10px]">{title}</p>
            {subtitle && <p className="text-xs text-[var(--pg-ink-muted)] mt-0.5 truncate">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div>{children}</div>
    </section>
  );
}

function MetricCard({ label, metric, icon: Icon, accent = 'emerald', hint }) {
  const live = metric?.live;
  const displayValue = formatValue(metric);
  const isObj = displayValue === null;
  return (
    <div
      data-testid="intel-metric-card"
      data-live={live ? 'true' : 'false'}
      className="pg-card p-4 sm:p-5 relative overflow-hidden"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-[var(--pg-ink-muted)]">{label}</p>
          {!isObj && (
            <p className="font-display text-2xl sm:text-3xl mt-2 text-[var(--pg-ink)] tracking-[0.02em]">{displayValue}</p>
          )}
          {isObj && Array.isArray(metric.value) && (
            <ul className="mt-2 space-y-1">
              {metric.value.slice(0, 3).map((it, i) => (
                <li key={i} className="text-xs text-[var(--pg-ink)] truncate">
                  <span className="font-mono text-[10px] text-[var(--pg-ink-faint)] mr-2">{String(i+1).padStart(2,'0')}</span>
                  {it.title || it.name || '—'}{it.revenue !== undefined ? ` · ${formatUsd(it.revenue)}` : ''}
                </li>
              ))}
            </ul>
          )}
          {(metric?.hint || hint) && (
            <p className="text-[10px] text-[var(--pg-ink-faint)] mt-2 font-mono tracking-wide">{metric?.hint || hint}</p>
          )}
          {!live && metric?.placeholder && (
            <p className="text-[10px] text-[var(--pg-gold-2)] mt-2 font-mono tracking-wide">⏳ {metric.placeholder}</p>
          )}
        </div>
        <span
          className="h-8 w-8 rounded-md flex items-center justify-center shrink-0 border border-[var(--pg-border-2)]"
          style={{ background: 'rgba(7,11,20,0.5)' }}
        >
          {Icon ? <Icon size={14} color={ACCENTS[accent]} /> : null}
        </span>
      </div>
      <span
        className="absolute top-2 right-2 text-[9px] font-mono tracking-[0.22em] px-1.5 py-0.5 rounded"
        style={{
          color: live ? ACCENTS.emerald : ACCENTS.gold,
          background: live ? 'rgba(25,194,142,0.08)' : 'rgba(226,199,122,0.08)',
        }}
      >
        {live ? 'LIVE' : 'FUTURE'}
      </span>
    </div>
  );
}

function ChartCard({ title, accent = 'emerald', children, live, future, testId }) {
  return (
    <div data-testid={testId} className="pg-card p-4 sm:p-5 relative overflow-hidden">
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-[var(--pg-ink-muted)]">{title}</p>
        <span
          className="text-[9px] font-mono tracking-[0.22em] px-1.5 py-0.5 rounded"
          style={{
            color: live ? ACCENTS.emerald : ACCENTS.gold,
            background: live ? 'rgba(25,194,142,0.08)' : 'rgba(226,199,122,0.08)',
          }}
        >
          {live ? 'LIVE' : 'FUTURE'}
        </span>
      </div>
      <div className="h-[180px] sm:h-[200px]" style={{ filter: future ? 'saturate(0.6)' : 'none' }}>
        {children}
      </div>
      {future && (
        <p className="text-[10px] text-[var(--pg-gold-2)] mt-2 font-mono tracking-wide">⏳ Future Integration — chart will activate when the underlying telemetry is wired.</p>
      )}
    </div>
  );
}

/* =====================================================
   Recommendations banner
   ===================================================== */
function RecommendationCard({ rec }) {
  const isFuture = rec.future || /future intelligent/i.test(rec.title || '');
  const accent = rec.level === 'high' ? 'pink' : rec.level === 'medium' ? 'gold' : 'emerald';
  return (
    <div
      data-testid="intel-recommendation"
      data-level={rec.level}
      className="pg-card p-4 relative overflow-hidden"
      style={{
        borderColor: isFuture ? 'rgba(226,199,122,0.25)' : `${ACCENTS[accent]}55`,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
          style={{
            background: isFuture ? 'rgba(226,199,122,0.15)' : ACCENTS[accent] + '22',
            color: isFuture ? ACCENTS.gold : ACCENTS[accent],
          }}
        >
          {isFuture ? <Hourglass size={13} /> : <Sparkles size={13} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono tracking-[0.24em] uppercase" style={{ color: isFuture ? ACCENTS.gold : ACCENTS[accent] }}>
              {rec.category || 'Signal'}
            </span>
            {!isFuture && (
              <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-[var(--pg-ink-faint)]">{rec.level || 'info'}</span>
            )}
          </div>
          <p className="font-serif text-base mt-1 text-[var(--pg-ink)] leading-tight">{rec.title}</p>
          <p className="text-xs text-[var(--pg-ink-muted)] mt-1 leading-relaxed">{rec.body}</p>
          {rec.action && (
            <a href={rec.action.href || '#'} className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.24em] uppercase" style={{ color: ACCENTS[accent] }}>
              {rec.action.label} <ArrowUpRight size={11} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   Main module
   ===================================================== */
export default function CommandIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get('/admin/intelligence/summary');
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load intelligence');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const recs = data?.recommendations || [];
  const exec = data?.executive || {};
  const music = data?.music || {};
  const podcasts = data?.podcasts || {};
  const mobileReads = data?.mobile_reads || {};
  const vault = data?.vault || {};
  const marketing = data?.marketing || {};
  const visitors = data?.visitors || {};
  const content = data?.content || {};
  const platform = data?.platform || {};

  // Synthesize light chart data so charts always render something (real where live, placeholder where future)
  const revenueSeries = useMemo(() => {
    // Use month/week/today values as a 3-point trend if available
    const mo = Number(vault?.revenue_this_month?.value) || 0;
    const wk = Number(vault?.revenue_this_week?.value) || 0;
    const td = Number(vault?.revenue_today?.value) || 0;
    return [
      { period: '30d ago', revenue: Math.max(mo - wk, 0) },
      { period: '7d', revenue: Math.max(wk - td, 0) },
      { period: 'Today', revenue: td },
    ];
  }, [vault]);

  const cmsActivityData = useMemo(() => {
    const c = platform?.cms_activity?.value || {};
    return [
      { name: 'Music', value: c.music || 0 },
      { name: 'Podcasts', value: c.podcasts || 0 },
      { name: 'Articles', value: c.articles || 0 },
      { name: 'Reads', value: c.mobile_reads || 0 },
      { name: 'Videos', value: c.videos || 0 },
      { name: 'Vault', value: c.products || 0 },
      { name: 'Posters', value: c.marketing_posters || 0 },
    ];
  }, [platform]);

  const placeholderStreams = useMemo(() => (
    [{ d: 'M', v: 0 }, { d: 'T', v: 0 }, { d: 'W', v: 0 }, { d: 'T', v: 0 }, { d: 'F', v: 0 }, { d: 'S', v: 0 }, { d: 'S', v: 0 }]
  ), []);

  if (loading) {
    return <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]" data-testid="intel-loading">Pulling signals…</div>;
  }
  if (err) {
    return <div className="pg-card p-10 text-center text-[var(--pg-neon-pink)]">{err}</div>;
  }

  return (
    <motion.div
      data-testid="command-intelligence"
      initial={{ opacity: 1, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* ======= Recommendations ======= */}
      <SectionShell
        icon={Sparkles}
        title="COMMAND INTELLIGENCE RECOMMENDATIONS"
        subtitle="Signals derived from current platform activity. Future-ready cards appear where telemetry is not yet wired."
        accent="gold"
        testId="intel-recommendations-section"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {recs.length === 0 && (
            <div className="pg-card p-4 col-span-full">
              <p className="text-sm text-[var(--pg-ink-muted)]">No recommendations yet. Publish more content to start generating signals.</p>
            </div>
          )}
          {recs.map((r) => <RecommendationCard key={r.id} rec={r} />)}
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={load} className="pg-btn-ghost inline-flex items-center gap-1.5 !py-1.5 !px-3 text-[10px] font-mono tracking-[0.24em] uppercase" data-testid="intel-refresh">
            <RefreshCcw size={11} /> Refresh signals
          </button>
        </div>
      </SectionShell>

      {/* ======= Executive Summary ======= */}
      <SectionShell icon={Activity} title="EXECUTIVE SUMMARY" subtitle="The view from orbit." accent="emerald" testId="intel-executive-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Active visitors" metric={exec.active_visitors} icon={Users} accent="emerald" />
          <MetricCard label="Streams today" metric={exec.streams_today} icon={MusicIcon} accent="emerald" />
          <MetricCard label="Podcast plays today" metric={exec.podcast_plays_today} icon={Mic} accent="emerald" />
          <MetricCard label="Revenue today" metric={exec.digital_product_revenue_today} icon={CircleDollarSign} accent="gold" />
          <MetricCard label="Orders today" metric={exec.orders_today} icon={ShoppingCart} accent="gold" />
          <MetricCard label="Active campaigns" metric={exec.active_marketing_campaigns} icon={Megaphone} accent="pink" />
          <MetricCard label="Scheduled releases" metric={exec.scheduled_releases} icon={Clock} accent="pink" />
          <MetricCard label="Top performing content" metric={exec.top_performing_content} icon={TrendingUp} accent="gold" />
        </div>
      </SectionShell>

      {/* ======= Music Intelligence ======= */}
      <SectionShell icon={MusicIcon} title="MUSIC INTELLIGENCE" subtitle="Streams, listeners, retention." accent="emerald" testId="intel-music-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Total streams" metric={music.total_streams} icon={Activity} />
          <MetricCard label="Streams today" metric={music.streams_today} icon={Activity} />
          <MetricCard label="Streams this week" metric={music.streams_this_week} icon={Activity} />
          <MetricCard label="Streams this month" metric={music.streams_this_month} icon={Activity} />
          <MetricCard label="Unique listeners" metric={music.unique_listeners} icon={Users} />
          <MetricCard label="Library size" metric={music.library_size} icon={Database} accent="emerald" />
          <MetricCard label="Top performing songs" metric={music.top_performing} icon={TrendingUp} accent="gold" />
          <MetricCard label="Average listening time" metric={music.average_listening_time} icon={Clock} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Weekly streams (placeholder)" live={false} future testId="intel-music-streams-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={placeholderStreams}>
                <defs>
                  <linearGradient id="streamsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#19c28e" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#19c28e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="d" stroke="#888" fontSize={10} />
                <YAxis stroke="#888" fontSize={10} />
                <Tooltip contentStyle={{ background: 'rgba(7,11,20,0.92)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11 }} />
                <Area type="monotone" dataKey="v" stroke="#19c28e" fill="url(#streamsGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Listener retention curve (placeholder)" live={false} future testId="intel-music-retention-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[{ s: 0, r: 100 }, { s: 25, r: 0 }, { s: 50, r: 0 }, { s: 75, r: 0 }, { s: 100, r: 0 }]}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="s" unit="%" stroke="#888" fontSize={10} />
                <YAxis stroke="#888" fontSize={10} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: 'rgba(7,11,20,0.92)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11 }} />
                <Line type="monotone" dataKey="r" stroke="#e2c77a" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <div className="mt-4">
          <MetricCard label="Recently published" metric={music.recently_published} icon={FileText} accent="emerald" />
        </div>
      </SectionShell>

      {/* ======= Podcast Intelligence ======= */}
      <SectionShell icon={Mic} title="PODCAST INTELLIGENCE" subtitle="Plays, episodes, completion." accent="emerald" testId="intel-podcast-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Total plays" metric={podcasts.total_plays} icon={Activity} />
          <MetricCard label="Plays today" metric={podcasts.plays_today} icon={Activity} />
          <MetricCard label="Plays this week" metric={podcasts.plays_this_week} icon={Activity} />
          <MetricCard label="Plays this month" metric={podcasts.plays_this_month} icon={Activity} />
          <MetricCard label="Library size" metric={podcasts.library_size} icon={Database} accent="emerald" />
          <MetricCard label="Top episodes" metric={podcasts.top_episodes} icon={TrendingUp} accent="gold" />
          <MetricCard label="Completion rate" metric={podcasts.episode_completion_rate} icon={BadgeCheck} />
          <MetricCard label="Avg listening duration" metric={podcasts.average_listening_duration} icon={Clock} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Plays — last 7 days (placeholder)" live={false} future testId="intel-podcast-plays-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={placeholderStreams}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="d" stroke="#888" fontSize={10} />
                <YAxis stroke="#888" fontSize={10} />
                <Tooltip contentStyle={{ background: 'rgba(7,11,20,0.92)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11 }} />
                <Bar dataKey="v" fill="#ff3ea5" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <MetricCard label="Recently published episodes" metric={podcasts.recently_published} icon={FileText} accent="emerald" />
        </div>
      </SectionShell>

      {/* ======= Mobile Reads Intelligence ======= */}
      <SectionShell icon={BookOpen} title="MOBILE READS INTELLIGENCE" subtitle="Books, journals, guides, workbooks." accent="gold" testId="intel-mobile-reads-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Library size" metric={mobileReads.library_size} icon={Database} accent="emerald" />
          <MetricCard label="New releases" metric={mobileReads.new_releases} icon={Star} accent="gold" />
          <MetricCard label="Free titles" metric={mobileReads.free_count} icon={BookOpen} accent="emerald" />
          <MetricCard label="Paid titles" metric={mobileReads.paid_count} icon={CircleDollarSign} accent="gold" />
          <MetricCard label="Featured titles" metric={mobileReads.featured} icon={Sparkles} accent="gold" />
          <MetricCard label="Downloads today" metric={mobileReads.downloads_today} icon={Activity} />
          <MetricCard label="Downloads lifetime" metric={mobileReads.downloads_lifetime} icon={Activity} />
          <MetricCard label="Average read time" metric={mobileReads.average_read_time} icon={Clock} />
          <MetricCard label="Completion rate" metric={mobileReads.completion_rate} icon={BadgeCheck} />
          <MetricCard label="Audio companions" metric={mobileReads.audio_companion_count || { value: null, live: false, placeholder: 'Add audio_url to a Mobile Read to count companions.' }} icon={Headphones} />
        </div>
      </SectionShell>

      {/* ======= Digital Product (Vault) Intelligence ======= */}
      <SectionShell icon={CircleDollarSign} title="DIGITAL PRODUCT INTELLIGENCE" subtitle="Stripe-backed revenue, orders, conversions." accent="gold" testId="intel-vault-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Revenue today" metric={vault.revenue_today} icon={CircleDollarSign} accent="gold" />
          <MetricCard label="Revenue this week" metric={vault.revenue_this_week} icon={CircleDollarSign} accent="gold" />
          <MetricCard label="Revenue this month" metric={vault.revenue_this_month} icon={CircleDollarSign} accent="gold" />
          <MetricCard label="Lifetime revenue" metric={vault.lifetime_revenue} icon={CircleDollarSign} accent="gold" />
          <MetricCard label="Orders today" metric={vault.orders_today} icon={ShoppingCart} />
          <MetricCard label="Orders lifetime" metric={vault.orders_lifetime} icon={ShoppingCart} />
          <MetricCard label="Conversion rate (30d)" metric={vault.conversion_rate} icon={TrendingUp} accent="emerald" />
          <MetricCard label="Pending orders" metric={vault.pending_orders} icon={AlertCircle} accent="pink" />
          <MetricCard label="Refund summary" metric={{ ...vault.refund_summary, value: vault.refund_summary?.value ? `${vault.refund_summary.value.count} · ${formatUsd(vault.refund_summary.value.amount)}` : '0 · $0.00' }} icon={RefreshCcw} accent="pink" />
          <MetricCard label="Catalogue size" metric={vault.catalogue_size} icon={Database} accent="emerald" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Revenue trend (month → today)" live={vault.lifetime_revenue?.live} testId="intel-vault-revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueSeries}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#e2c77a" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#e2c77a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="period" stroke="#888" fontSize={10} />
                <YAxis stroke="#888" fontSize={10} />
                <Tooltip contentStyle={{ background: 'rgba(7,11,20,0.92)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11 }} formatter={(v) => formatUsd(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#e2c77a" fill="url(#revGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <MetricCard label="Best-selling products" metric={vault.best_selling} icon={TrendingUp} accent="gold" />
        </div>
      </SectionShell>

      {/* ======= Marketing Intelligence ======= */}
      <SectionShell icon={Megaphone} title="MARKETING INTELLIGENCE" subtitle="Campaign reach, click-through, conversion." accent="pink" testId="intel-marketing-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Poster impressions" metric={marketing.poster_impressions} icon={Activity} />
          <MetricCard label="Poster clicks" metric={marketing.poster_clicks} icon={Activity} />
          <MetricCard label="Click-through rate" metric={marketing.click_through_rate} icon={TrendingUp} />
          <MetricCard label="Top campaign" metric={marketing.top_performing_campaign} icon={Sparkles} accent="pink" />
          <MetricCard label="Campaign conversions" metric={marketing.campaign_conversions} icon={BadgeCheck} />
          <MetricCard label="Active campaigns" metric={marketing.active_campaigns} icon={Megaphone} accent="pink" />
          <MetricCard label="Scheduled campaigns" metric={marketing.scheduled_campaigns} icon={Clock} accent="gold" />
          <MetricCard label="Expired campaigns" metric={marketing.expired_campaigns} icon={Hourglass} />
        </div>
      </SectionShell>

      {/* ======= Visitor Intelligence ======= */}
      <SectionShell icon={Users} title="VISITOR INTELLIGENCE" subtitle="Audience, retention, devices, geography." accent="emerald" testId="intel-visitor-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Total visitors" metric={visitors.total_visitors} icon={Users} />
          <MetricCard label="Returning visitors" metric={visitors.returning_visitors} icon={Users} />
          <MetricCard label="New visitors" metric={visitors.new_visitors} icon={Users} />
          <MetricCard label="Newsletter subscribers" metric={visitors.newsletter_subscribers} icon={BadgeCheck} accent="emerald" />
          <MetricCard label="Avg session duration" metric={visitors.average_session_duration} icon={Clock} />
          <MetricCard label="Most visited channels" metric={visitors.most_visited_channels} icon={TrendingUp} />
          <MetricCard label="Device breakdown" metric={visitors.device_breakdown} icon={Activity} />
          <MetricCard label="Geographic overview" metric={visitors.geographic_overview} icon={Activity} />
        </div>
        <ChartCard title="Devices (placeholder)" live={false} future testId="intel-devices-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={[{ name: 'Desktop', value: 60 }, { name: 'Mobile', value: 30 }, { name: 'Tablet', value: 10 }]} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40}>
                <Cell fill="#19c28e" />
                <Cell fill="#e2c77a" />
                <Cell fill="#ff3ea5" />
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: 'rgba(7,11,20,0.92)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </SectionShell>

      {/* ======= Content Intelligence ======= */}
      <SectionShell icon={FileText} title="CONTENT INTELLIGENCE" subtitle="What people consume and how often." accent="gold" testId="intel-content-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Most played songs" metric={content.most_played_songs} icon={MusicIcon} />
          <MetricCard label="Most played podcasts" metric={content.most_played_podcasts} icon={Mic} />
          <MetricCard label="Most viewed articles" metric={content.most_viewed_articles} icon={FileText} />
          <MetricCard label="Most downloaded products" metric={content.most_downloaded_products} icon={ShoppingCart} />
          <MetricCard label="Trending content" metric={content.trending_content} icon={TrendingUp} />
        </div>
        <RecentlyPublished data={content.recently_published?.value} />
      </SectionShell>

      {/* ======= Platform Intelligence ======= */}
      <SectionShell icon={Server} title="PLATFORM INTELLIGENCE" subtitle="CMS activity, storage, runtime." accent="emerald" testId="intel-platform-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <MetricCard label="Scheduled publications" metric={platform.scheduled_publications} icon={Clock} accent="gold" />
          <MetricCard label="Storage usage" metric={platform.storage_usage} icon={Database} />
          <MetricCard label="Runtime status" metric={platform.runtime_status} icon={BadgeCheck} accent="emerald" />
          <MetricCard label="System health" metric={{ ...platform.system_health, value: platform.system_health?.value ? Object.entries(platform.system_health.value).map(([k,v]) => `${k}:${v}`).join(' · ') : '—' }} icon={Activity} accent="emerald" />
        </div>
        <ChartCard title="CMS activity by surface" live testId="intel-cms-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cmsActivityData}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke="#888" fontSize={10} />
              <YAxis stroke="#888" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'rgba(7,11,20,0.92)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11 }} />
              <Bar dataKey="value" fill="#19c28e" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </SectionShell>

      <p className="text-[10px] font-mono tracking-[0.24em] text-[var(--pg-ink-faint)] text-center mt-6 uppercase" data-testid="intel-generated-at">
        Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : ''}
      </p>
    </motion.div>
  );
}

function RecentlyPublished({ data }) {
  if (!data) return null;
  const rails = [
    { key: 'music', label: 'Music', accent: 'emerald' },
    { key: 'podcasts', label: 'Podcasts', accent: 'emerald' },
    { key: 'articles', label: 'Articles', accent: 'gold' },
    { key: 'products', label: 'Products', accent: 'pink' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {rails.map((r) => (
        <div key={r.key} className="pg-card p-4">
          <p className="text-[10px] font-mono tracking-[0.28em] uppercase" style={{ color: ACCENTS[r.accent] }}>{r.label}</p>
          <ul className="mt-2 space-y-1.5">
            {(data[r.key] || []).length === 0 ? (
              <li className="text-xs text-[var(--pg-ink-faint)] italic">Nothing published yet</li>
            ) : (
              (data[r.key] || []).map((it, i) => (
                <li key={it.id || i} className="text-xs text-[var(--pg-ink)] truncate">
                  <span className="font-mono text-[10px] text-[var(--pg-ink-faint)] mr-2">{String(i+1).padStart(2,'0')}</span>
                  {it.title}
                </li>
              ))
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}
